// 🚀 Blog Category to Product Category Mapping
// Maps blog post categories to relevant product categories for intelligent recommendations

import { turfProblemIntent } from '@/lib/blog-intent'

export interface BlogProductMapping {
  blogCategory: string
  productCategories: string[]
  priority: 'high' | 'medium' | 'low'
  description: string
}

// Comprehensive mapping between blog categories and product categories
export const BLOG_PRODUCT_MAPPINGS: BlogProductMapping[] = [
  {
    blogCategory: "Grass Types",
    productCategories: ["Grass Seed & Lawn Repair", "Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools"],
    priority: "high",
    description: "Products for identifying, planting, and maintaining specific grass types"
  },
  {
    blogCategory: "Lawn Care Tips", 
    productCategories: ["Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools", "Smart Lawn Tech & Automation"],
    priority: "high",
    description: "General lawn maintenance products and tools"
  },
  {
    blogCategory: "Watering",
    productCategories: ["Watering & Irrigation", "Smart Lawn Tech & Automation", "Fertilizers & Soil Health"],
    priority: "high",
    description: "Irrigation systems, sprinklers, and water management tools"
  },
  {
    blogCategory: "Fertilizing",
    productCategories: ["Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools"],
    priority: "high", 
    description: "Fertilizers, soil amendments, and application tools"
  },
  {
    blogCategory: "Mowing & Maintenance",
    productCategories: ["Lawn Mowers & Maintenance Tools", "Smart Lawn Tech & Automation"],
    priority: "high",
    description: "Mowers, trimmers, and maintenance equipment"
  },
  {
    blogCategory: "Pest Control",
    productCategories: ["Pest & Disease Control", "Weed Control"],
    priority: "high",
    description: "Pesticides, herbicides, and pest management solutions"
  },
  {
    blogCategory: "Seasonal Care",
    productCategories: ["Fertilizers & Soil Health", "Grass Seed & Lawn Repair"],
    priority: "high",
    description: "Season-specific treatments and preparation products"
  },
  {
    blogCategory: "Climate & Regional Tips",
    productCategories: ["Grass Seed & Lawn Repair", "Fertilizers & Soil Health", "Watering & Irrigation"],
    priority: "medium",
    description: "Climate-adapted grass varieties and regional care products"
  },
  {
    blogCategory: "Tech & Tools",
    productCategories: ["Smart Lawn Tech & Automation", "Lawn Mowers & Maintenance Tools"],
    priority: "high",
    description: "Smart lawn technology and advanced tools"
  },
  {
    blogCategory: "Problem Solving",
    productCategories: ["Pest & Disease Control", "Fertilizers & Soil Health", "Grass Seed & Lawn Repair"],
    priority: "high",
    description: "Problem diagnosis and solution products"
  },
  {
    blogCategory: "Weed Control",
    productCategories: ["Weed Control", "Pest & Disease Control"],
    priority: "high",
    description: "Herbicides and weed prevention products"
  },
  {
    blogCategory: "Disease Control",
    productCategories: ["Pest & Disease Control", "Fertilizers & Soil Health"],
    priority: "high",
    description: "Fungicides and disease prevention products"
  },
  {
    blogCategory: "Organic Lawn Care",
    productCategories: ["Fertilizers & Soil Health"],
    priority: "medium",
    description: "Natural and organic lawn care products"
  },
  {
    blogCategory: "Soil Health",
    productCategories: ["Fertilizers & Soil Health"],
    priority: "high",
    description: "Soil amendments, testing kits, and health products"
  }
]

// Slug-level overrides for more relevant product recommendations. Some posts sit in
// a blog category whose default product mapping mismatches the post's actual intent
// (a weed/disease "how to get rid of it" post categorized under Grass Types or Seasonal
// Care would otherwise draw a grass-seed or fertilizer card). Steer those to the
// category that matches what the reader needs to buy.
const SLUG_PRODUCT_OVERRIDES: Record<string, string> = {
  'st-augustinegrass-guide': 'Pest & Disease Control',
  'fastest-growing-grass-varieties': 'Grass Seed & Lawn Repair',
  'best-lowmaintenance-grass-types': 'Grass Seed & Lawn Repair',
  'best-grass-for-hightraffic-areas': 'Grass Seed & Lawn Repair',
  'how-to-identify-your-grass-type-by-look-feel': 'Grass Seed & Lawn Repair',
  // Weed-ID/removal posts sitting under Grass Types or Seeding → show weed control, not seed.
  'how-to-identify-quack-grass-and-why-its-taking-over-your-lawn': 'Weed Control',
  'how-to-identify-poa-annua-annual-bluegrass': 'Weed Control',
  'clover-seed-identification-removal-guide': 'Weed Control',
  // Turf-disease posts under Seasonal Care → show fungicide/disease control, not fertilizer.
  'winter-lawn-mold-prevention-treatment': 'Pest & Disease Control',
  'preventing-spring-dead-spot-winter-essential-steps-healthy-lawn': 'Pest & Disease Control',
  // Seed/repair posts miscategorized under Seasonal Care → show seed & repair.
  'dormant-seeding-guide': 'Grass Seed & Lawn Repair',
  'how-to-fix-bare-spots-in-your-lawn-before-winter': 'Grass Seed & Lawn Repair',
  'fix-patchy-lawn-spots-after-winter-easy-solutions': 'Grass Seed & Lawn Repair',
  // Species-selection posts under Climate & Regional → show seed, not fertilizer.
  'best-grass-for-texas-lawns': 'Grass Seed & Lawn Repair',
  'native-grass-types-for-your-region': 'Grass Seed & Lawn Repair',
}

/**
 * Map a blog category (and optional slug) to a single Directus product category.
 * Slug overrides take priority over category-based mapping.
 */
export function mapBlogToProductCategory(blogCategory: string, slug?: string): string {
  if (slug && SLUG_PRODUCT_OVERRIDES[slug]) return SLUG_PRODUCT_OVERRIDES[slug]

  // Turf-disease and turf-insect posts draw from Pest & Disease Control whatever
  // their blog category says. Most of them sit under "Lawn Care Tips", which maps
  // to fertilizer, so a gray leaf spot or armyworm reader was being shown lawn
  // food. The rule generalizes the one-off disease slugs already listed in
  // SLUG_PRODUCT_OVERRIDES above, which stay because an explicit slug still wins.
  if (turfProblemIntent(slug, blogCategory)) return 'Pest & Disease Control'

  const mapping: Record<string, string> = {
    'Grass Types': 'Grass Seed & Lawn Repair',
    'Seasonal Care': 'Fertilizers & Soil Health',
    'Fertilizing': 'Fertilizers & Soil Health',
    'Watering': 'Watering & Irrigation',
    'Weed Control': 'Weed Control',
    'Pest Control': 'Pest & Disease Control',
    'Mowing & Maintenance': 'Lawn Mowers & Maintenance Tools',
    'Seeding/Overseeding & Establishment': 'Grass Seed & Lawn Repair',
    'Tech & Tools': 'Lawn Mowers & Maintenance Tools',
    'Problem Solving': 'Fertilizers & Soil Health',
    'Lawn Care Tips': 'Fertilizers & Soil Health',
    'Climate & Regional Tips': 'Fertilizers & Soil Health',
  }
  return mapping[blogCategory] || 'Fertilizers & Soil Health'
}

/**
 * Get recommended product categories for a blog post category
 */
export function getProductCategoriesForBlog(blogCategory: string): string[] {
  const mapping = BLOG_PRODUCT_MAPPINGS.find(
    m => m.blogCategory.toLowerCase() === blogCategory.toLowerCase()
  )
  
  if (mapping) {
    return mapping.productCategories
  }
  
  // Default fallback categories for general lawn care
  return ["Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools", "Grass Seed & Lawn Repair"]
}

/**
 * Get priority level for blog category recommendations
 */
export function getBlogCategoryPriority(blogCategory: string): 'high' | 'medium' | 'low' {
  const mapping = BLOG_PRODUCT_MAPPINGS.find(
    m => m.blogCategory.toLowerCase() === blogCategory.toLowerCase()
  )
  
  return mapping?.priority || 'medium'
}

/**
 * Get mapping description for a blog category
 */
export function getBlogCategoryDescription(blogCategory: string): string {
  const mapping = BLOG_PRODUCT_MAPPINGS.find(
    m => m.blogCategory.toLowerCase() === blogCategory.toLowerCase()
  )
  
  return mapping?.description || "Related lawn care products and tools"
}

/**
 * Get all available blog categories that have product mappings
 */
export function getAvailableBlogCategories(): string[] {
  return BLOG_PRODUCT_MAPPINGS.map(m => m.blogCategory)
}

/**
 * Search for relevant product categories using fuzzy matching
 */
export function searchProductCategories(searchTerm: string): string[] {
  const allCategories = new Set<string>()
  
  // Add categories from exact matches
  BLOG_PRODUCT_MAPPINGS.forEach(mapping => {
    if (mapping.blogCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mapping.description.toLowerCase().includes(searchTerm.toLowerCase())) {
      mapping.productCategories.forEach(cat => allCategories.add(cat))
    }
  })
  
  // If no matches found, return general categories
  if (allCategories.size === 0) {
    return ["Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools", "Grass Seed & Lawn Repair"]
  }
  
  return Array.from(allCategories)
}
